import React, { useEffect, useState } from 'react'
import Editor from '@monaco-editor/react'
import { motion } from 'framer-motion'
import { FiSave, FiCode, FiAlertCircle } from 'react-icons/fi'
import { VscError, VscWarning, VscInfo } from 'react-icons/vsc'
import { FaFire } from 'react-icons/fa'
import toast from 'react-hot-toast'

const MonacoEditor = ({ file, onSave, isDark }) => {
  const [code, setCode] = useState(file?.content || '')
  const [isSaving, setIsSaving] = useState(false)
  const [errors, setErrors] = useState([])
  const [warnings, setWarnings] = useState([])
  const [language, setLanguage] = useState('javascript')
  const [editorKey, setEditorKey] = useState(0)

  useEffect(() => {
    if (file) {
      setCode(file.content || '')
      const detectedLang = detectLanguage(file.name)
      setLanguage(detectedLang)
      setEditorKey(prev => prev + 1) // Force re-render
      analyzeCode(file.content, detectedLang)
    }
  }, [file])

  const detectLanguage = (filename) => {
    const ext = filename.split('.').pop().toLowerCase()
    const languageMap = {
      js: 'javascript',
      jsx: 'javascript',
      ts: 'typescript',
      tsx: 'typescript',
      py: 'python',
      html: 'html',
      css: 'css',
      json: 'json',
      java: 'java',
      cpp: 'cpp',
      c: 'cpp',
      php: 'php',
      rb: 'ruby',
      go: 'go',
      rs: 'rust',
      md: 'markdown',
      txt: 'plaintext',
      xml: 'xml',
      yml: 'yaml',
      yaml: 'yaml',
      sql: 'sql',
      sh: 'shell'
    }
    return languageMap[ext] || 'plaintext'
  }

  const analyzeCode = (content, lang) => {
    // Simple code analysis
    const newErrors = []
    const newWarnings = []

    if (lang === 'javascript') {
      try {
        // Check for syntax errors
        new Function(content)
      } catch (err) {
        newErrors.push({
          line: 1,
          message: err.message,
          severity: 'error'
        })
      }

      // Check for common issues
      const lines = content.split('\n')
      lines.forEach((line, index) => {
        if (line.includes('console.log') && !line.trim().startsWith('//')) {
          newWarnings.push({
            line: index + 1,
            message: 'Consider removing console.log in production',
            severity: 'warning'
          })
        }
        
        if (line.includes('var ') && !line.trim().startsWith('//')) {
          newWarnings.push({
            line: index + 1,
            message: 'Consider using const or let instead of var',
            severity: 'info'
          })
        }
      })
    }

    setErrors(newErrors)
    setWarnings(newWarnings)
  }

  const handleSave = async () => {
    if (!file) return

    setIsSaving(true)
    try {
      const updatedFile = {
        ...file,
        content: code,
        updatedAt: new Date().toISOString()
      }
      await onSave(updatedFile)
      toast.success('File saved successfully! 💾')
    } catch (error) {
      toast.error('Failed to save file')
    } finally {
      setIsSaving(false)
    }
  }

  const handleEditorChange = (value) => {
    setCode(value || '')
    analyzeCode(value, language)
  }

  const handleEditorDidMount = (editor, monaco) => {
    // Configure editor options
    editor.updateOptions({
      minimap: { enabled: true },
      scrollBeyondLastLine: false,
      fontSize: 14,
      lineHeight: 1.6,
      fontFamily: "'Fira Code', 'Cascadia Code', Consolas, Monaco, 'Courier New', monospace",
      wordWrap: 'on',
      automaticLayout: true,
      tabSize: 2,
      insertSpaces: true,
      formatOnPaste: true,
      formatOnType: true,
      suggestOnTriggerCharacters: true,
      acceptSuggestionOnEnter: 'on',
      snippetSuggestions: 'inline',
      parameterHints: { enabled: true },
      links: true,
      contextmenu: true,
      quickSuggestions: {
        other: true,
        comments: true,
        strings: true
      },
      suggest: {
        showFields: true,
        showFunctions: true,
        showVariables: true,
        showClasses: true,
        showConstructors: true,
        showModules: true,
        showProperties: true,
        showEvents: true,
        showOperators: true,
        showUnits: true,
        showValues: true,
        showConstants: true,
        showEnums: true,
        showEnumMembers: true,
        showKeywords: true,
        showWords: true,
        showColors: true,
        showFiles: true,
        showReferences: true,
        showFolders: true,
        showTypeParameters: true,
        showSnippets: true
      }
    })

    // Custom theme for red color scheme
    monaco.editor.defineTheme('codefire-red', {
      base: isDark ? 'vs-dark' : 'vs',
      inherit: true,
      rules: [
        { token: 'keyword', foreground: '#ef4444' },
        { token: 'string', foreground: '#dc2626' },
        { token: 'comment', foreground: '#9ca3af' },
        { token: 'number', foreground: '#f97316' },
        { token: 'function', foreground: '#9333ea' },
        { token: 'variable', foreground: '#3b82f6' },
        { token: 'type', foreground: '#10b981' },
      ],
      colors: {
        'editor.background': isDark ? '#111827' : '#ffffff',
        'editor.foreground': isDark ? '#f3f4f6' : '#1f2937',
        'editor.lineHighlightBackground': isDark ? '#1f2937' : '#f3f4f6',
        'editorCursor.foreground': '#ef4444',
        'editor.selectionBackground': isDark ? '#374151' : '#fee2e2',
        'editor.inactiveSelectionBackground': isDark ? '#37415180' : '#fee2e280',
        'editor.lineHighlightBorder': isDark ? '#374151' : '#f3f4f6',
      }
    })
    
    monaco.editor.setTheme('codefire-red')

    // Add custom IntelliSense
    monaco.languages.registerCompletionItemProvider(language, {
      provideCompletionItems: (model, position) => {
        const word = model.getWordUntilPosition(position)
        const suggestions = getSuggestions(language, word.word)
        
        return {
          suggestions: suggestions.map(suggestion => ({
            label: suggestion.label,
            kind: suggestion.kind,
            insertText: suggestion.insertText,
            detail: suggestion.detail,
            documentation: suggestion.documentation
          }))
        }
      }
    })

    // Add keyboard shortcuts
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.KeyS, handleSave)
    editor.addCommand(monaco.KeyMod.CtrlCmd | monaco.KeyCode.Space, () => {
      editor.trigger('keyboard', 'editor.action.triggerSuggest', {})
    })
  }

  const getSuggestions = (lang, currentWord) => {
    const commonSuggestions = [
      {
        label: 'console.log',
        kind: 0,
        insertText: 'console.log(${1:value});',
        detail: 'Log to console',
        documentation: 'Outputs a message to the console'
      },
      {
        label: 'function',
        kind: 1,
        insertText: 'function ${1:name}(${2:params}) {\n\t${3:// code}\n}',
        detail: 'Function declaration',
        documentation: 'Declares a new function'
      },
      {
        label: 'const',
        kind: 14,
        insertText: 'const ${1:name} = ${2:value};',
        detail: 'Constant declaration',
        documentation: 'Declares a constant variable'
      },
      {
        label: 'let',
        kind: 14,
        insertText: 'let ${1:name} = ${2:value};',
        detail: 'Variable declaration',
        documentation: 'Declares a mutable variable'
      }
    ]

    if (lang === 'javascript') {
      return [
        ...commonSuggestions,
        {
          label: 'if',
          kind: 14,
          insertText: 'if (${1:condition}) {\n\t${2:// code}\n}',
          detail: 'If statement',
          documentation: 'Conditional statement'
        },
        {
          label: 'for',
          kind: 14,
          insertText: 'for (let ${1:i} = 0; ${1:i} < ${2:length}; ${1:i}++) {\n\t${3:// code}\n}',
          detail: 'For loop',
          documentation: 'Iteration statement'
        },
        {
          label: 'while',
          kind: 14,
          insertText: 'while (${1:condition}) {\n\t${2:// code}\n}',
          detail: 'While loop',
          documentation: 'While iteration statement'
        }
      ]
    }

    return commonSuggestions
  }

  if (!file) {
    return (
      <div className="h-full flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-20 h-20 mx-auto mb-4 rounded-full bg-gradient-to-r from-primary-100 to-red-100 dark:from-gray-800 dark:to-primary-900/20 flex items-center justify-center">
            <FaFire className="text-3xl text-primary-600 dark:text-primary-400 animate-pulse" />
          </div>
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-2">
            No file selected
          </h3>
          <p className="text-gray-600 dark:text-gray-400 max-w-sm mx-auto">
            Select a file from the sidebar or create a new one to start coding
          </p>
        </div>
      </div>
    )
  }

  return (
    <div className="h-full flex flex-col bg-white dark:bg-gray-900">
      {/* Editor Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50">
        <div className="flex items-center space-x-3">
          <div className="flex items-center space-x-2">
            <FiCode className="text-primary-600 dark:text-primary-400" />
            <span className="font-mono text-sm font-medium text-gray-900 dark:text-white">
              {file.name}
            </span>
          </div>
          <div className="px-2 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 text-xs font-medium rounded">
            {language.toUpperCase()}
          </div>
        </div>

        <div className="flex items-center space-x-4">
          {/* Issues */}
          {(errors.length > 0 || warnings.length > 0) && (
            <div className="flex items-center space-x-2">
              {errors.length > 0 && (
                <div className="flex items-center space-x-1 text-red-600 dark:text-red-400">
                  <VscError />
                  <span className="text-xs">{errors.length}</span>
                </div>
              )}
              {warnings.length > 0 && (
                <div className="flex items-center space-x-1 text-yellow-600 dark:text-yellow-400">
                  <VscWarning />
                  <span className="text-xs">{warnings.length}</span>
                </div>
              )}
            </div>
          )}

          {/* Save Button */}
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="flex items-center space-x-2 px-4 py-2 rounded-lg bg-gradient-to-r from-primary-500 to-red-500 text-white hover:from-primary-600 hover:to-red-600 disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg"
          >
            {isSaving ? (
              <>
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                <span>Saving...</span>
              </>
            ) : (
              <>
                <FiSave />
                <span>Save</span>
                <span className="text-xs opacity-80">(Ctrl+S)</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Editor */}
      <div className="flex-1 overflow-hidden">
        <Editor
          key={`${editorKey}-${isDark}`}
          height="100%"
          language={language}
          value={code}
          theme={isDark ? 'codefire-red' : 'codefire-red'}
          onChange={handleEditorChange}
          onMount={handleEditorDidMount}
          options={{
            automaticLayout: true,
            fontSize: 14,
            lineNumbers: 'on',
            roundedSelection: false,
            scrollBeyondLastLine: false,
            readOnly: false,
            wordWrap: 'on',
            minimap: { enabled: true },
            suggestOnTriggerCharacters: true,
            parameterHints: { enabled: true },
            formatOnPaste: true,
            formatOnType: true,
          }}
        />
      </div>

      {/* Status Bar */}
      <div className="px-4 py-2 border-t border-gray-200 dark:border-gray-800 bg-gray-50 dark:bg-gray-800/50 flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-1">
            <FiAlertCircle className="w-3 h-3" />
            <span>{language.toUpperCase()}</span>
          </div>
          <div>
            Lines: {code.split('\n').length}
          </div>
          <div>
            Characters: {code.length}
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <div className="flex items-center space-x-1">
            <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse" />
            <span>Ready</span>
          </div>
          {isSaving && (
            <div className="flex items-center space-x-1 text-primary-600 dark:text-primary-400">
              <div className="w-3 h-3 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
              <span>Saving...</span>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default MonacoEditor